import { constants as config } from '@config';
import { configureDispatchers, addProjectFacadeListeners } from 'src/store/dispatchers';

import initGlobalInkive from '../../global-inkive';

import { CredentialsInterceptor } from './credentials-interceptor';
import checkBrowserVersion from './browser-checker';
import initializeComponents from './components-initializer';
import loadComponents from './components-loader';
import displayConditionalFeatures from './conditional-features';
import getEditor from './editor-initializer';
import onload from './onload';
import {
  preloadTranslations,
  preloadVersionFile,
  preloadUserData,
  initPageFactory,
  initAdvancedOrderedListFactory,
  initBulletListFactory
} from './preload';

let initialLoad;
let asyncLoadInterval;

(async function () {
  try {
    const credentialsInterceptor = new CredentialsInterceptor();
    credentialsInterceptor.registerInterceptors();

    initialLoad = (async () => {
      await jQueryDocumentReady();

      credentialsInterceptor.overrideGlobalCredentials();

      await checkBrowserVersion();
      await Promise.all([
        preloadTranslations(),
        preloadVersionFile(),
        preloadUserData()
      ]);
    })();

    initGlobalInkive();

    const projectReadyPromise = projectReady();

    await initialLoad;
    await projectReadyPromise;

    initPageFactory();
    displayConditionalFeatures();
    const editor = getEditor();
    const tool = await getToolInstance(editor);
    loadComponents();
    initAdvancedOrderedListFactory();
    initBulletListFactory();
    editor.init();
    initializeComponents(tool, editor);
    require('../factory/page-thumbnail');

    onload();
    configureDispatchers();
  } catch (err) {
    console.error(err);
    displayLoadingError(err);
  }
})();

function projectReady () {
  if (!INKIVE.C.BOOK || INKIVE.C.BOOK.cookiesInitialized) {
    return new Promise(resolve => {
      INKIVE.load = async function ({ template, design }) {
        const encodedId = design || template;
        await consolidateAsyncLoader(encodedId);
        await initialLoad;
        clearAsyncLoader();

        INKIVE.C.DEFAULT_FINEUPLOADER.uploadSuccess.params.scrapbookId = encodedId;
        INKIVE.C.BOOK = {
          id: encodedId,
          encodedId,
          book_type: template
            ? INKIVE.C.BOOK_TYPE.TEMPLATE
            : INKIVE.C.BOOK_TYPE.DESYGNER_DESIGN
        };

        const ProjectFacade = require('../facade/project/project');
        addProjectFacadeListeners(ProjectFacade);
        initPageFactory();
        const info = await ProjectFacade.getInfo();

        INKIVE.C.BOOK.book_type = info.book_type;
        INKIVE.C.BUCKET = info.master_bucket;
        INKIVE.C.BOOK.thumb = info.scrapbook_url_key;
        INKIVE.C.BOOK.create_date = info.create_date;
        INKIVE.C.BOOK.name = Base64.encode(info.name);
        INKIVE.C.BOOK.description = Base64.encode(info.description);
        INKIVE.C.BOOK.category = info.category;
        INKIVE.C.BOOK.security = info.security_val;
        INKIVE.C.BOOK.external_reference = info.cover_layout;
        INKIVE.C.BOOK.initialized = true;

        resolve();
      };

      const projectLoadData = getProjectLoadData();

      projectLoadData
        ? INKIVE.load(projectLoadData)
        : addLoadingArgumentListener();
    });
  } else {
    INKIVE.C.BOOK.initialized = true;
  }
}

function setAddons (addons) {
  const extendedEditing = addons?.extended_editing?.features || [];
  const whiteLabelFeatures = addons?.white_label?.features || [];
  const whiteLabelConfig = addons?.white_label?.config || {};
  const whiteLabelColors = { ...whiteLabelConfig.colors } || {};
  const currentColors = INKIVE.C.WHITE_LABEL_CONFIGURATION.colors;

  Object.assign(INKIVE.C.WHITE_LABEL_CONFIGURATION, whiteLabelConfig);
  Object.assign(INKIVE.C.WHITE_LABEL_CONFIGURATION.colors, currentColors, whiteLabelColors);

  INKIVE.C.ADDONS = addons;
  INKIVE.C.SHOW_ANIMATION = whiteLabelFeatures.includes('show_animation');
  INKIVE.C.SHOW_LAYOUT = extendedEditing.includes('show_layout');
  INKIVE.C.SHOW_TEXT_OVERFLOW = extendedEditing.includes('show_text_overflow');
  INKIVE.C.SHOW_NEW_EDITOR = !!addons.new_editor;

  INKIVE.C.WHITE_LABEL_CONFIGURATION.supports_sso = addons?.sso?.features?.includes?.('login_gateway') ?? false;
  INKIVE.C.WHITE_LABEL_CONFIGURATION.sso_only = addons?.sso?.features?.includes?.('sso_only') ?? false;
}

function setEditorSettings (settings, urlParams) {
  const { page, zoom } = settings;

  if (zoom) {
    INKIVE.zoom = zoom;
    urlParams.set('zoom', zoom);
  }

  if (page) {
    INKIVE.page = page;
    urlParams.set('p', page);
  }
}

async function setLoaderThumbnail (thumbnail) {
  await jQueryDocumentReady();

  const loader = document.querySelector('#inkiveLoader');
  const image = loader.querySelector('img');
  const frame = loader.querySelector('.frame');

  if (INKIVE.C.IS_MOBILE) {
    frame.style.paddingLeft = '0px';
    frame.style.paddingTop = '0px';
  }

  const thumbnailImage = new Promise(resolve => {
    const image = new Image();
    image.src = thumbnail.src;
    image.onerror = () => resolve(null);
    image.onload = () => resolve(image);
  });

  loader.classList.add('thumbnail');

  let projectSize = null;
  const size = thumbnail.size;
  if (size && size.width && size.height && size.unit) {
    const toPixels = INKIVE.Helpers.convertUnitsToPixels;

    projectSize = {
      width: toPixels(size.width, size.unit),
      height: toPixels(size.height, size.unit)
    };
  } else {
    const image = await thumbnailImage;
    projectSize = image ? { width: image.width, height: image.height } : null;
  }

  const whitePixel = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  image.setAttribute('src', whitePixel);
  thumbnailImage.then(img => img && image.setAttribute('src', img.src));

  if (projectSize) {
    const containerSize = getContainerSize();

    const containerRatio = containerSize.width / containerSize.height;
    const imageRatio = projectSize.width / projectSize.height;

    if (containerRatio > imageRatio) {
      image.setAttribute('width', containerSize.height * imageRatio);
      image.setAttribute('height', containerSize.height);
    } else {
      image.setAttribute('width', containerSize.width);
      image.setAttribute('height', containerSize.width / imageRatio);
    }
  }

  function getContainerSize () {
    const frame = loader.querySelector('.frame');
    const frameStyle = frame.currentStyle || window.getComputedStyle(frame);
    const imageStyle = image.currentStyle || window.getComputedStyle(image);
    const containerSize = { width: loader.clientWidth, height: loader.clientHeight };

    containerSize.height -= parseFloat(frameStyle.paddingTop);
    containerSize.height -= parseFloat(frameStyle.paddingBottom);
    containerSize.height -= parseFloat(imageStyle.marginTop);
    containerSize.height -= parseFloat(imageStyle.marginBottom);

    containerSize.width -= parseFloat(frameStyle.paddingLeft);
    containerSize.width -= parseFloat(frameStyle.paddingRight);
    containerSize.width -= parseFloat(imageStyle.marginLeft);
    containerSize.width -= parseFloat(imageStyle.marginRight);

    return containerSize;
  }
}

function getLoadingArgument (name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length > 1) return parts.pop().split(';').shift();
}

function removeLoadingArgument (name) {
  const domain = '.' + window.location.hostname.match(/([^.]+.[^.]+)$/)[0];

  document.cookie = name + '=; path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  document.cookie = name + '=; path=/; domain=' + domain + '; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

async function consolidateAsyncLoader (encodedId) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('id', encodedId);

  const print = (getLoadingArgument(config.cookies.loadPrintOptionKey) === 'true') ? '&print' : '';
  INKIVE.C.REVIEW_MODE = (getLoadingArgument(config.cookies.loadReviewModeOptionKey) === 'true') ? '&reviewMode' : null;

  const backUrl = getLoadingArgument(config.cookies.loadBackUrlOptionKey);
  backUrl && urlParams.set('back_url', backUrl);

  INKIVE.C.TRANSLATION_DATA = getLoadingArgument(config.cookies.loadTranslationDataOptionKey);
  INKIVE.C.TRANSLATION_DATA = INKIVE.C.TRANSLATION_DATA ? JSON.parse(INKIVE.C.TRANSLATION_DATA) : null;

  const referrer = getLoadingArgument(config.cookies.loadReferrerOptionKey);
  referrer && urlParams.set('referrer', referrer);

  const settings = getLoadingArgument(config.cookies.loadViewOptionKey);
  settings && setEditorSettings(JSON.parse(settings), urlParams);

  const addons = getLoadingArgument(config.cookies.loadAddonsOptionKey);
  addons && setAddons(JSON.parse(addons));

  const workspaceConfiguration = getLoadingArgument(config.cookies.loadWorkspaceOptionKey);
  workspaceConfiguration && Object.assign(
    INKIVE.C.WHITE_LABEL_CONFIGURATION,
    JSON.parse(workspaceConfiguration)
  );

  INKIVE.C.initialBackgroundImageSource = getLoadingArgument(config.cookies.loadBackgroundUrlOptionKey);

  window.history.replaceState({}, '', `?${urlParams}${print}`);

  delete INKIVE.load;
  asyncLoadInterval && clearInterval(asyncLoadInterval);

  const thumbnail = getLoadingArgument(config.cookies.loadThumbnailOptionKey);
  thumbnail && await setLoaderThumbnail(JSON.parse(thumbnail));
}

function clearAsyncLoader () {
  removeLoadingArgument(config.cookies.loadPrintOptionKey);
  removeLoadingArgument(config.cookies.loadReferrerOptionKey);
  removeLoadingArgument(config.cookies.loadThumbnailOptionKey);
  removeLoadingArgument(config.cookies.loadKey);
  removeLoadingArgument(config.cookies.loadAddonsOptionKey);
  removeLoadingArgument(config.cookies.loadWorkspaceOptionKey);
  removeLoadingArgument(config.cookies.loadReviewModeOptionKey);
  removeLoadingArgument(config.cookies.loadTranslationDataOptionKey);
  removeLoadingArgument(config.cookies.loadViewOptionKey);
  removeLoadingArgument(config.cookies.loadBackUrlOptionKey);
}

function addLoadingArgumentListener () {
  asyncLoadInterval = setInterval(() => {
    const projectLoadData = getProjectLoadData();
    projectLoadData && INKIVE.load && INKIVE.load(projectLoadData);
  }, 500);
}

function getProjectLoadData () {
  const projectLoadData = getLoadingArgument(config.cookies.loadKey);
  return projectLoadData ? JSON.parse(projectLoadData) : null;
}

function jQueryDocumentReady () {
  return new Promise(resolve => $(document).ready(resolve));
}

function displayLoadingError (err) {
  const $loader = $('#inkiveLoader');
  $('body').addClass('browser-not-supported');
  $loader.length && $loader.append(err);
}

function getToolInstance (editor) {
  return new Promise(resolve => {
    const tool = INKIVE.Tool.getInstance(null, editor, () => resolve(tool));
  });
}
